import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_MY_QUESTION_SETS, ADD_QUESTION_TO_SET } from '../graphql/profile';

const AddToLibraryModal = ({ 
  isOpen, 
  onClose, 
  question,
  onSuccess 
}) => {
  const [optionScores, setOptionScores] = useState([]);
  const [weightExponent, setWeightExponent] = useState(0); // 0-10, weight = 2^weightExponent
  const [isRequired, setIsRequired] = useState(false);
  const [unansweredRequiredScore, setUnansweredRequiredScore] = useState(0);

  const [addQuestionToSet] = useMutation(ADD_QUESTION_TO_SET);
  
  const { data: questionSetsData } = useQuery(GET_MY_QUESTION_SETS, {
    skip: !isOpen,
    fetchPolicy: 'cache-and-network'
  });

  const questionSets = questionSetsData?.myQuestionSets?.questionSets || [];
  const defaultQuestionSet = questionSets.find(qs => qs.isDefault);

  // 初始化分值
  React.useEffect(() => {
    if (question && isOpen) {
      const initialScores = question.options?.map((_, index) => ({
        optionIndex: index,
        score: 0
      })) || [];
      setOptionScores(initialScores);
      setWeightExponent(0);
      setIsRequired(false);
      setUnansweredRequiredScore(0);
    }
  }, [question, isOpen]);

  // 映射滑動條位置到實際分數
  const mapSliderToScore = (sliderValue) => {
    if (sliderValue <= -11) return -9999;  // 左端極值
    return sliderValue;                    // -10 到 +10 之間保持原值
  };
  
  // 映射實際分數到滑動條位置
  const mapScoreToSlider = (score) => {
    if (score <= -9999) return -12;  // 極小值映射到左端
    if (score > 10) return 10;       // 超過10的映射到右端
    return score;                    // -10 到 +10 之間保持原值
  };

  const handleOptionScoreChange = (index, sliderValue) => {
    const actualScore = mapSliderToScore(parseFloat(sliderValue));
    setOptionScores(prev => prev.map((item, i) => 
      i === index ? { ...item, score: actualScore } : item
    ));
  };

  const handleSubmit = async () => {
    try {
      if (!defaultQuestionSet) {
        alert('找不到預設題庫');
        return;
      }

      const weight = Math.pow(2, weightExponent);

      const addResult = await addQuestionToSet({
        variables: {
          input: {
            questionSetId: defaultQuestionSet.id,
            questionId: question.id,
            scoring: {
              optionScores: optionScores.map(s => ({
                optionIndex: s.optionIndex,
                score: s.score
              })),
              weight: weight,
              isRequired: isRequired,
              unansweredRequiredScore: unansweredRequiredScore
            },
            order: defaultQuestionSet.questionCount || 0
          }
        }
      });

      if (!addResult.data.addQuestionToSet.success) {
        alert(`加入擇友標準失敗：${addResult.data.addQuestionToSet.errors?.map(e => e.message).join(', ')}`);
      } else {
        // 根據後端返回的訊息顯示對應的成功提示
        const message = addResult.data.addQuestionToSet.message || '操作成功';
        if (message === '已更新') {
          alert('已更新擇友標準！');
        } else {
          alert('已成功加入擇友標準！');
        }
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }
    } catch (error) {
      console.error('加入擇友標準失敗:', error);
      alert('加入擇友標準失敗：' + error.message);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 32px;
          border-radius: 3px;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 32px;
          border-radius: 3px;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        
        /* 負分 - 灰色滑塊 */
        input[type="range"].slider-negative::-webkit-slider-thumb {
          background: #9ca3af;
        }
        
        input[type="range"].slider-negative::-moz-range-thumb {
          background: #9ca3af;
        }
        
        input[type="range"].slider-negative:hover::-webkit-slider-thumb {
          background: #6b7280;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        input[type="range"].slider-negative:hover::-moz-range-thumb {
          background: #6b7280;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        /* 正分 - 黃色滑塊 */
        input[type="range"].slider-positive::-webkit-slider-thumb {
          background: #fbbf24;
        }
        
        input[type="range"].slider-positive::-moz-range-thumb {
          background: #fbbf24;
        }
        
        input[type="range"].slider-positive:hover::-webkit-slider-thumb {
          background: #f59e0b;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        input[type="range"].slider-positive:hover::-moz-range-thumb {
          background: #f59e0b;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        /* 權重滑塊 - 動態顏色 */
        input[type="range"].slider-weight::-webkit-slider-thumb {
          background: var(--slider-color);
        }
        
        input[type="range"].slider-weight::-moz-range-thumb {
          background: var(--slider-color);
        }
        
        input[type="range"].slider-weight:hover::-webkit-slider-thumb {
          filter: brightness(0.85);
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        input[type="range"].slider-weight:hover::-moz-range-thumb {
          filter: brightness(0.85);
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        /* -9999 極值 - 紅色滑塊 */
        input[type="range"].slider-extreme::-webkit-slider-thumb {
          background: #dc2626;
        }
        
        input[type="range"].slider-extreme::-moz-range-thumb {
          background: #dc2626;
        }
        
        input[type="range"].slider-extreme:hover::-webkit-slider-thumb {
          background: #b91c1c;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
        
        input[type="range"].slider-extreme:hover::-moz-range-thumb {
          background: #b91c1c;
          box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        }
      `}</style>
      
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-purple-400">
        {/* 標題 */}
          <div className="px-6 py-4 border-b border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="text-2xl mr-2">📚</span>
              加入擇友標準
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        {/* 內容 */}
        <div className="px-6 py-4 w-full overflow-hidden">
          {/* 問題資訊 */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 w-full overflow-hidden">
              <h4 className="font-semibold text-lg text-gray-900 mb-2 break-words">{question?.title || '未知問題'}</h4>
              {question?.description && (
                <p className="text-sm text-gray-600 mb-2 break-words whitespace-pre-wrap">{question.description}</p>
              )}
              {question?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {question.tags.map((tag, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      {tag.name || tag}
                    </span>
                  ))}
                </div>
              )}
          </div>

          {/* 分值設定 */}
            {defaultQuestionSet ? (
              <div className="space-y-4 w-full overflow-hidden">
                {/* 權重設置 */}
                <div className="w-full overflow-hidden">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    權重: <span className="text-purple-600 font-bold">{Math.pow(2, weightExponent)}</span>
                  </label>
                  {/* 權重計量條 */}
                  {(() => {
                    // 計算漸變：0 = 黑灰，10 = 黃色
                    const ratio = weightExponent / 10;
                    const bgR = Math.round(229 + (254 - 229) * ratio);
                    const bgG = Math.round(231 + (249 - 231) * ratio);
                    const bgB = Math.round(235 + (195 - 235) * ratio);
                    const bgColor = `rgb(${bgR}, ${bgG}, ${bgB})`;
                    
                    const barR = Math.round(156 + (251 - 156) * ratio);
                    const barG = Math.round(163 + (191 - 163) * ratio);
                    const barB = Math.round(175 + (36 - 175) * ratio);
                    const barColor = `rgb(${barR}, ${barG}, ${barB})`;
                    
                    const sliderR = Math.round(31 + (245 - 31) * ratio);
                    const sliderG = Math.round(41 + (158 - 41) * ratio);
                    const sliderB = Math.round(55 + (11 - 55) * ratio);
                    const sliderColor = `rgb(${sliderR}, ${sliderG}, ${sliderB})`;
                    
                    return (
                      <div className="relative border border-gray-300 rounded-lg overflow-hidden h-10 w-full" style={{ backgroundColor: bgColor }}>
                        <div
                          className="absolute top-0 left-0 bottom-0"
                          style={{
                            width: `${(weightExponent / 10) * 100}%`,
                            backgroundColor: barColor
                          }}
                        ></div>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={weightExponent}
                          onChange={(e) => setWeightExponent(parseInt(e.target.value))}
                          className="relative z-10 w-full h-full opacity-60 hover:opacity-100 cursor-pointer slider-weight"
                          style={{
                            background: 'transparent',
                            WebkitAppearance: 'none',
                            appearance: 'none',
                            '--slider-color': sliderColor
                          }}
                        />
                      </div>
                    );
                  })()}
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span>1024</span>
                  </div>
                </div>
                
                {/* 必答題設置 */}
                <div className="w-full overflow-hidden">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    設為必答題
                  </label>
                  <div className="flex gap-2 w-full">
                    <button
                      type="button"
                      onClick={() => setIsRequired(true)}
                      className={`flex-1 px-4 py-2 text-sm rounded-lg border-2 transition-colors ${
                        isRequired
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      是
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsRequired(false)}
                      className={`flex-1 px-4 py-2 text-sm rounded-lg border-2 transition-colors ${
                        !isRequired
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      否
                    </button>
                  </div>
                </div>

                {/* 未作答必答題分數設定（僅在設為必答題時顯示） */}
                {isRequired && (
                  <div className="w-full overflow-hidden p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      未作答必答題分數
                    </label>
                    {(() => {
                      const sliderValue = mapScoreToSlider(unansweredRequiredScore);
                      const isPositive = unansweredRequiredScore >= 0;
                      
                      // 計算0分的位置百分比（滑動條範圍 -12 到 10）
                      const sliderMin = -12;
                      const sliderMax = 10;
                      const sliderRange = sliderMax - sliderMin;
                      const zeroPosition = ((0 - sliderMin) / sliderRange) * 100;
                      
                      // 計算計量條寬度百分比
                      let percentage;
                      if (unansweredRequiredScore <= -9999) {
                        percentage = zeroPosition;
                      } else if (isPositive) {
                        percentage = (unansweredRequiredScore / 10) * (100 - zeroPosition);
                      } else {
                        percentage = (Math.abs(unansweredRequiredScore) / Math.abs(sliderMin)) * zeroPosition;
                      }
                      
                      return (
                        <div className="space-y-1 w-full overflow-hidden">
                          <div className="flex items-start justify-between text-sm gap-2 w-full">
                            <span className="text-gray-700 font-medium flex-1 min-w-0 break-words">
                              當此題目設為必答但未作答時，將使用此分數計算
                            </span>
                            <span className={`font-bold whitespace-nowrap flex-shrink-0 ${
                              unansweredRequiredScore === -9999 ? 'text-red-600' : isPositive ? 'text-purple-600' : 'text-gray-600'
                            }`}>
                              {unansweredRequiredScore === -9999 ? '-9999' : unansweredRequiredScore > 0 ? `+${unansweredRequiredScore.toFixed(2)}` : unansweredRequiredScore.toFixed(2)}分
                            </span>
                          </div>
                          
                          {/* 計量條預覽 */}
                          <div className="relative border border-gray-300 rounded-lg overflow-hidden h-8 w-full">
                            {/* 中線 - 顯示在0分位置 */}
                            <div 
                              className="absolute top-0 bottom-0 w-px bg-gray-400 z-0"
                              style={{ left: `${zeroPosition}%` }}
                            ></div>
                            {unansweredRequiredScore !== 0 && (
                              <div
                                className={`absolute top-0 bottom-0 z-0 ${
                                  isPositive ? 'bg-yellow-100' : 'bg-gray-200'
                                }`}
                                style={{
                                  [isPositive ? 'left' : 'right']: `${isPositive ? zeroPosition : (100 - zeroPosition)}%`,
                                  width: `${percentage}%`
                                }}
                              ></div>
                            )}
                            
                            {/* 滑動條 */}
                            <input
                              type="range"
                              min="-12"
                              max="10"
                              step="0.5"
                              value={sliderValue}
                              onChange={(e) => {
                                const actualScore = mapSliderToScore(parseFloat(e.target.value));
                                setUnansweredRequiredScore(actualScore);
                              }}
                              className={`relative z-10 w-full h-full opacity-50 hover:opacity-100 cursor-pointer ${
                                unansweredRequiredScore === -9999 ? 'slider-extreme' : isPositive ? 'slider-positive' : 'slider-negative'
                              }`}
                              style={{
                                background: 'transparent',
                                WebkitAppearance: 'none',
                                appearance: 'none'
                              }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* 選項評分 */}
                <div className="w-full overflow-hidden">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    選項配分
                  </label>
                  <div className="space-y-3 w-full overflow-hidden">
                    {question?.options?.map((option, index) => {
                      const currentScore = optionScores[index]?.score || 0;
                      const sliderValue = mapScoreToSlider(currentScore);
                      const isPositive = currentScore >= 0;
                      
                      // 計算0分的位置百分比（滑動條範圍 -12 到 10）
                      const sliderMin = -12;
                      const sliderMax = 10;
                      const sliderRange = sliderMax - sliderMin;
                      const zeroPosition = ((0 - sliderMin) / sliderRange) * 100;
                      
                      // 計算計量條寬度百分比
                      let percentage;
                      if (currentScore <= -9999) {
                        percentage = zeroPosition;
                      } else if (isPositive) {
                        percentage = (currentScore / 10) * (100 - zeroPosition);
                      } else {
                        // 負分：從左邊緣向零點延伸，-12時達到零點位置，-10時比例減少
                        percentage = (Math.abs(currentScore) / Math.abs(sliderMin)) * zeroPosition;
                      }
                      
                      return (
                        <div key={index} className="space-y-1 w-full overflow-hidden">
                          <div className="flex items-start justify-between text-sm gap-2 w-full">
                            <span className="text-gray-700 font-medium flex-1 min-w-0 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                              {index + 1}. {option.text}
                            </span>
                            <span className={`font-bold whitespace-nowrap flex-shrink-0 ${
                              currentScore === -9999 ? 'text-red-600' : isPositive ? 'text-purple-600' : 'text-gray-600'
                            }`}>
                              {currentScore === -9999 ? '-9999' : currentScore > 0 ? `+${currentScore.toFixed(2)}` : currentScore.toFixed(2)}分
                            </span>
                          </div>
                          
                          {/* 計量條預覽 */}
                          <div className="relative border border-gray-300 rounded-lg overflow-hidden h-8 w-full">
                            {/* 中線 - 顯示在0分位置 */}
                            <div 
                              className="absolute top-0 bottom-0 w-px bg-gray-400 z-0"
                              style={{ left: `${zeroPosition}%` }}
                            ></div>
                            {currentScore !== 0 && (
                              <div
                                className={`absolute top-0 bottom-0 z-0 ${
                                  isPositive ? 'bg-yellow-100' : 'bg-gray-200'
                                }`}
                                style={{
                                  [isPositive ? 'left' : 'right']: `${isPositive ? zeroPosition : (100 - zeroPosition)}%`,
                                  width: `${percentage}%`
                                }}
                              ></div>
                            )}
                            
                            {/* 滑動條 */}
                            <input
                              type="range"
                              min="-12"
                              max="10"
                              step="0.5"
                              value={sliderValue}
                              onChange={(e) => handleOptionScoreChange(index, e.target.value)}
                              className={`relative z-10 w-full h-full opacity-50 hover:opacity-100 cursor-pointer ${
                                currentScore === -9999 ? 'slider-extreme' : isPositive ? 'slider-positive' : 'slider-negative'
                              }`}
                              style={{
                                background: 'transparent',
                                WebkitAppearance: 'none',
                                appearance: 'none'
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
                請先登入
              </div>
            )}
        </div>

        {/* 按鈕區域 */}
          <div className="px-6 py-4 border-t border-purple-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!defaultQuestionSet}
              className="px-4 py-2 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-lg hover:from-pink-500 hover:to-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            加入擇友標準
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default AddToLibraryModal;

